import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getApi, postApi, putApi, deleteApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";
import { toast } from "react-toastify";

// Get all checklist templates
export const getChecklistTemplates = createAsyncThunk(
    "checklistTemplate/getChecklistTemplates",
    async (params = {}, { rejectWithValue }) => {
        try {
            const queryString = new URLSearchParams(params).toString();
            const url = queryString
                ? `${API_PATHS.CHECKLIST_TEMPLATES}?${queryString}`
                : API_PATHS.CHECKLIST_TEMPLATES;
            const response = await getApi(url);
            return response.data;
        } catch (error) {
            toast.error("Failed to fetch checklist templates");
            return rejectWithValue(error.message);
        }
    }
);

// Get single checklist template
export const getChecklistTemplate = createAsyncThunk(
    "checklistTemplate/getChecklistTemplate",
    async (id, { rejectWithValue }) => {
        try {
            const response = await getApi(`${API_PATHS.CHECKLIST_TEMPLATES}/${id}`);
            return response.data.data;
        } catch (error) {
            toast.error("Failed to fetch checklist template");
            return rejectWithValue(error.message);
        }
    }
);

// Create checklist template
export const createChecklistTemplate = createAsyncThunk(
    "checklistTemplate/createChecklistTemplate",
    async ({ values, resetForm }, { dispatch, rejectWithValue }) => {
        try {
            const response = await postApi(API_PATHS.CHECKLIST_TEMPLATES, values);
            toast.success("Checklist template created successfully");

            // Refresh the list
            await dispatch(getChecklistTemplates());

            if (resetForm) resetForm();
            return response.data.data;
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to create checklist template");
            return rejectWithValue(error.message);
        }
    }
);

// Update checklist template
export const updateChecklistTemplate = createAsyncThunk(
    "checklistTemplate/updateChecklistTemplate",
    async ({ id, values }, { dispatch, rejectWithValue }) => {
        try {
            const response = await putApi(`${API_PATHS.CHECKLIST_TEMPLATES}/${id}`, values);
            toast.success("Checklist template updated successfully");

            // Refresh the list
            await dispatch(getChecklistTemplates());

            return response.data.data;
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update checklist template");
            return rejectWithValue(error.message);
        }
    }
);

// Delete checklist template
export const deleteChecklistTemplate = createAsyncThunk(
    "checklistTemplate/deleteChecklistTemplate",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            await deleteApi(`${API_PATHS.CHECKLIST_TEMPLATES}/`, id);
            toast.success("Checklist template deleted successfully");

            // Refresh the list
            await dispatch(getChecklistTemplates());

            return id;
        } catch (error) {
            toast.error("Failed to delete checklist template");
            return rejectWithValue(error.message);
        }
    }
);

// Delete many checklist templates
export const deleteManyChecklistTemplates = createAsyncThunk(
    "checklistTemplate/deleteManyChecklistTemplates",
    async (ids, { dispatch, rejectWithValue }) => {
        try {
            await postApi(`${API_PATHS.CHECKLIST_TEMPLATES}/deleteMany`, ids);
            toast.success("Checklist templates deleted successfully");

            // Refresh the list
            await dispatch(getChecklistTemplates());

            return ids;
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to delete checklist templates");
            return rejectWithValue(error.message);
        }
    }
);

const checklistTemplateSlice = createSlice({
    name: "checklistTemplate",
    initialState: {
        data: [],
        currentTemplate: null,
        metadata: {
            totalRecords: 0,
            totalPages: 0,
            page: 1,
            limit: 10
        },
        loading: false,
        createLoading: false,
        updateLoading: false,
        deleteLoading: false,
        error: null,
    },
    reducers: {
        clearCurrentTemplate: (state) => {
            state.currentTemplate = null;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Get all templates
            .addCase(getChecklistTemplates.pending, (state) => {
                state.loading = true;
            })
            .addCase(getChecklistTemplates.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload.data || [];
                state.metadata = action.payload.metadata || state.metadata;
            })
            .addCase(getChecklistTemplates.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Get single template
            .addCase(getChecklistTemplate.pending, (state) => {
                state.loading = true;
            })
            .addCase(getChecklistTemplate.fulfilled, (state, action) => {
                state.loading = false;
                state.currentTemplate = action.payload;
            })
            .addCase(getChecklistTemplate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Create template
            .addCase(createChecklistTemplate.pending, (state) => {
                state.createLoading = true;
            })
            .addCase(createChecklistTemplate.fulfilled, (state, action) => {
                state.createLoading = false;
            })
            .addCase(createChecklistTemplate.rejected, (state, action) => {
                state.createLoading = false;
                state.error = action.payload;
            })

            // Update template
            .addCase(updateChecklistTemplate.pending, (state) => {
                state.updateLoading = true;
            })
            .addCase(updateChecklistTemplate.fulfilled, (state, action) => {
                state.updateLoading = false;
            })
            .addCase(updateChecklistTemplate.rejected, (state, action) => {
                state.updateLoading = false;
                state.error = action.payload;
            })

            // Delete template
            .addCase(deleteChecklistTemplate.pending, (state) => {
                state.deleteLoading = true;
            })
            .addCase(deleteChecklistTemplate.fulfilled, (state, action) => {
                state.deleteLoading = false;
            })
            .addCase(deleteChecklistTemplate.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload;
            })

            // Delete many templates
            .addCase(deleteManyChecklistTemplates.pending, (state) => {
                state.deleteLoading = true;
            })
            .addCase(deleteManyChecklistTemplates.fulfilled, (state, action) => {
                state.deleteLoading = false;
            })
            .addCase(deleteManyChecklistTemplates.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearCurrentTemplate, clearError } = checklistTemplateSlice.actions;
export default checklistTemplateSlice.reducer;
